import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Upload, X, AlertTriangle, Check } from 'lucide-react';
import { useUploadManager } from '../contexts/UploadManagerContext';
import RenameDuplicateModal from './RenameDuplicateModal';
import { useLanguage } from '../i18n';

const ACTIVE_STATUSES = new Set(['pending', 'creating', 'uploading', 'processing']);
const FINISHED_STATUSES = new Set(['completed', 'failed', 'partial']);

/**
 * Header-level upload indicator.
 *
 * Replaces the old floating "UploadTray" overlay so the input field stays
 * unobstructed. Lives in the chat header (next to the search icon) and
 * persists across conversation switches because the UploadManager state is
 * a global context provider.
 *
 *  - Hidden entirely when no upload is in-flight AND none have failed since
 *    the last manual dismiss.
 *  - Renders an Upload icon wrapped by a circular SVG progress ring that
 *    fills based on real upload progress (aggregated across in-flight rows).
 *  - Clicking opens a small popover with the per-upload list (active rows
 *    on top, then any failed rows pending acknowledgement).
 *  - Successfully completed rows auto-clear after a short delay so the icon
 *    disappears at 100% — matching the spec acceptance criterion. Failed
 *    rows stay until the user dismisses them.
 */
/**
 * Renders the spinner via viewBox alone (no fixed width/height attrs) so the
 * SVG stretches to fill its parent and the circle is always perfectly
 * concentric with whatever icon sits behind it. The parent decides the
 * pixel size — keeps the icon and the ring locked at the same centre point
 * regardless of button padding or browser baseline quirks.
 */
const CircularProgress = ({ percent = 0, viewBox = 32, stroke = 2 }) => {
    const radius = (viewBox - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const clamped = Math.max(0, Math.min(100, percent || 0));
    const offset = circumference * (1 - clamped / 100);
    return (
        <svg
            viewBox={`0 0 ${viewBox} ${viewBox}`}
            preserveAspectRatio="xMidYMid meet"
            className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none"
        >
            <circle
                cx={viewBox / 2}
                cy={viewBox / 2}
                r={radius}
                stroke="currentColor"
                strokeWidth={stroke}
                fill="none"
                className="text-pai-secondary text-pai-faint"
            />
            <circle
                cx={viewBox / 2}
                cy={viewBox / 2}
                r={radius}
                stroke="currentColor"
                strokeWidth={stroke}
                fill="none"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                className="text-blue-500 dark:text-blue-400 transition-[stroke-dashoffset] duration-300 ease-out"
            />
        </svg>
    );
};

const UploadRow = ({ upload, onDismiss, onRename }) => {
    const { t } = useLanguage();
    const active = ACTIVE_STATUSES.has(upload.status);
    const failed = upload.status === 'failed';
    const partial = upload.status === 'partial';
    const duplicate = upload.status === 'duplicate';
    const done = upload.status === 'completed';
    return (
        <div className="px-3 py-2 border-b border-pai-hairline last:border-0">
            <div className="flex items-start gap-2">
                <div className="flex-shrink-0 mt-0.5">
                    {done && <Check size={14} className="text-emerald-500" />}
                    {(failed || partial || duplicate) && <AlertTriangle size={14} className="text-amber-500" />}
                    {active && (
                        <div className="relative w-4 h-4">
                            <CircularProgress percent={upload.progress || 0} viewBox={16} stroke={2} />
                        </div>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-white truncate" title={upload.name}>
                        {upload.name}
                    </div>
                    <div className={`text-[11px] mt-0.5 ${
                        failed ? 'text-rose-600 dark:text-rose-400'
                        : (partial || duplicate) ? 'text-amber-600 dark:text-amber-400'
                        : done ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-pai-meta'
                    }`}>
                        {active && (upload.status === 'uploading'
                            ? `${upload.progress || 0}%`
                            : (upload.currentFile
                                ? t('fmProcessingCount', 'Processing {current}/{total}', { current: upload.current || 0, total: upload.total || 0 })
                                : t('fmProcessingEllipsis', 'Processing…')))}
                        {done && (upload.extractedCount !== undefined
                            ? t('fmExtractedFiles', 'Extracted {n} files', { n: upload.extractedCount })
                            : upload.type === 'folder'
                                ? t('fmUploadedCount', 'Uploaded {success}/{total} files', { success: upload.successCount, total: upload.total })
                                : t('fmUploaded', 'Uploaded'))}
                        {partial && t('fmPartialUploadStatus', '{success}/{total} uploaded · {failed} failed', { success: upload.successCount, total: upload.total, failed: upload.failureCount })}
                        {duplicate && (upload.error || t('fmDuplicateNameMessage', 'A document with this name already exists. Please provide a unique name.'))}
                        {failed && (upload.error || t('fmUploadFailed', 'Upload failed'))}
                    </div>
                    {partial && upload.error && (
                        <div className="text-[11px] mt-0.5 text-pai-meta break-words" data-testid="upload-partial-detail">
                            {upload.error}
                        </div>
                    )}
                    {duplicate && (
                        <button
                            onClick={() => onRename(upload)}
                            className="mt-1.5 px-2 py-0.5 text-[11px] font-medium rounded border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                            title={t('fmRenameAndRetry', 'Rename and retry')}
                        >
                            {t('commonRename', 'Rename')}
                        </button>
                    )}
                </div>
                {(failed || partial || duplicate) && (
                    <button
                        onClick={() => onDismiss(upload.id)}
                        className="p-0.5 text-pai-faint hover:text-white  flex-shrink-0"
                        title={t('fmDismiss', 'Dismiss')}
                    >
                        <X size={12} />
                    </button>
                )}
            </div>
        </div>
    );
};

const HeaderUploadIndicator = () => {
    const { t } = useLanguage();
    const { uploads, dismiss, renameDuplicate } = useUploadManager();
    const [open, setOpen] = useState(false);
    const [renameTarget, setRenameTarget] = useState(null);
    const wrapperRef = useRef(null);

    const active = useMemo(() => uploads.filter((u) => ACTIVE_STATUSES.has(u.status)), [uploads]);
    const failedOrPartial = useMemo(
        () => uploads.filter((u) => u.status === 'failed' || u.status === 'partial' || u.status === 'duplicate'),
        [uploads],
    );
    const recentlyDone = useMemo(
        () => uploads.filter((u) => u.status === 'completed'),
        [uploads],
    );

    // Auto-dismiss completed rows so the header indicator disappears at 100%
    // (per the spec). Failed/partial rows stay until acknowledged manually.
    useEffect(() => {
        if (recentlyDone.length === 0) return;
        const timers = recentlyDone.map((u) => {
            const since = Date.now() - (u.finishedAt || Date.now());
            const delay = Math.max(0, 4000 - since);
            return setTimeout(() => dismiss(u.id), delay);
        });
        return () => timers.forEach(clearTimeout);
    }, [recentlyDone, dismiss]);

    useEffect(() => {
        if (!open) return undefined;
        const onClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    if (active.length === 0 && failedOrPartial.length === 0) return null;

    // Aggregate progress across in-flight rows. We average the per-upload
    // `progress` (0-100) — the FileManager already updates `progress`
    // proportionally on folder uploads and byte-percent on single files, so
    // a simple mean is reasonably honest.
    const aggregatePct = active.length === 0
        ? 100
        : Math.round(active.reduce((sum, u) => sum + (u.progress || 0), 0) / active.length);

    const hasFailures = failedOrPartial.length > 0;

    return (
        <div ref={wrapperRef} className="relative">
            <button
                onClick={() => setOpen((v) => !v)}
                className={`relative w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                    hasFailures
                        ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                        : 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                }`}
                title={active.length > 0
                    ? t('fmUploadingItemsPct', 'Uploading {n} item{s} ({pct}%)', { n: active.length, s: active.length === 1 ? '' : 's', pct: aggregatePct })
                    : hasFailures
                        ? t('fmUploadsNeedAttention', '{n} upload{s} need attention', { n: failedOrPartial.length, s: failedOrPartial.length === 1 ? '' : 's' })
                        : t('fmUploads', 'Uploads')}
            >
                {/* Icon sits in the flex-centered slot — guaranteed centre of the
                    button's 1:1 box. The progress ring lives in an absolutely
                    positioned overlay that fills the same box, so the two share
                    a single mathematical centre. */}
                <Upload size={18} className="relative z-10 block" />
                {active.length > 0 && (
                    <CircularProgress percent={aggregatePct} viewBox={40} stroke={2.5} />
                )}
                {hasFailures && active.length === 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full" />
                )}
            </button>

            {open && (
                <div className="absolute right-0 mt-2 w-80 max-h-[60dvh] overflow-hidden bg-white/[0.03] border border-pai rounded-lg shadow-modal-sm z-50 flex flex-col">
                    <div className="px-3 py-2 border-b border-pai-subtle flex items-center justify-between">
                        <span className="text-sm font-semibold text-white">{t('fmUploads', 'Uploads')}</span>
                        <span className="text-[11px] text-pai-meta">
                            {active.length > 0 && t('fmActiveCount', '{n} active', { n: active.length })}
                            {active.length > 0 && hasFailures && ' · '}
                            {hasFailures && t('fmNeedAttentionCount', '{n} need attention', { n: failedOrPartial.length })}
                        </span>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {active.map((u) => <UploadRow key={u.id} upload={u} onDismiss={dismiss} onRename={setRenameTarget} />)}
                        {failedOrPartial.map((u) => <UploadRow key={u.id} upload={u} onDismiss={dismiss} onRename={setRenameTarget} />)}
                    </div>
                </div>
            )}

            {renameTarget && (
                <RenameDuplicateModal
                    filename={renameTarget.name}
                    message={renameTarget.error}
                    onSubmit={(newName) => { renameDuplicate(renameTarget.id, newName); setRenameTarget(null); }}
                    onCancel={() => { dismiss(renameTarget.id); setRenameTarget(null); }}
                />
            )}
        </div>
    );
};

export default HeaderUploadIndicator;
